import { Cip30Wallet } from "@hyperionbt/helios";
import * as Sentry from "@sentry/nextjs";
import { Artwork } from "kreate-common/modules/business-types";
import { CLIENT_ENV } from "kreate-env/client";
import { buildPartialTx$UpdateArtwork } from "kreate-protocol/offchain/tx/updateArtwork";
import { httpGet$GetArtwork } from "kreate-server/commands/GetArtwork/fetcher";
import { httpGet$GetTxStatus } from "kreate-server/commands/GetTxStatus/fetcher";
import { MdFavorite } from "react-icons/md";

import { assert } from "@/modules/common-utils/assert";
import { waitUntil } from "@/modules/common-utils/async";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  getAssetFingerprint,
  getPoolPmUrl,
} from "@/modules/kreate-components/components/LinkPoolPm/utils";
import { waitModal } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import ModalEnableMinting$Prompt, {
  ModalEnableMinting$Prompt$SuccessEvent,
} from "@/modules/kreate-modals/modals/ModalEnableMinting$Prompt";
import ModalProgressV2 from "@/modules/kreate-modals/modals/ModalProgressV2";
import { anticipateSteps } from "@/modules/kreate-modals/modals/ModalProgressV2/utils";
import ModalSuccess from "@/modules/kreate-modals/modals/ModalSuccess";

const steps = anticipateSteps({
  buildingTransaction: 2500,
  waitingForSignatureAndSubmission: 8500,
  waitingForConfirmation: 24500,
  waitingForIndexer: 12500,
});

type Params = {
  initialMintPrice: number;
  initialNumEditions: number;
  cip30Wallet: Cip30Wallet;
  artwork: Artwork;
};

export async function* runFlow$UpdateMintingDetails({
  initialMintPrice,
  initialNumEditions,
  cip30Wallet,
  artwork,
}: Params) {
  addBreadcrumb("waiting for user inputs...");
  const formData = yield* waitModal<ModalEnableMinting$Prompt$SuccessEvent>(
    (resolve, reject) => (
      <ModalEnableMinting$Prompt
        open
        useCase="update"
        initialMintPrice={initialMintPrice}
        initialNumEditions={initialNumEditions}
        onSuccess={(event) => resolve(event)}
        onCancel={() => reject(null)}
      />
    )
  );

  assert(
    artwork.utxo?.datum.type === "artwork",
    "not eligible for updating minting details"
  );

  addBreadcrumb("building transaction...");
  yield (
    <ModalProgressV2
      open
      content="Building transaction..."
      ratio={steps.buildingTransaction.ratio}
      expectedDuration={steps.buildingTransaction.duration}
    />
  );

  const { tx } = await buildPartialTx$UpdateArtwork({
    network: CLIENT_ENV.CARDANO_NETWORK,
    wallet: cip30Wallet,
    artworkUTxO: artwork.utxo,
    newArtworkDatum: {
      ...artwork.utxo?.datum.value,
      mintFee: formData.mintPrice,
      numEditions: formData.numEditions,
    },
  });

  addBreadcrumb("waiting for signature and submission...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for signature and submission..."
      ratio={steps.waitingForSignatureAndSubmission.ratio}
      expectedDuration={steps.waitingForSignatureAndSubmission.duration}
    />
  );

  const signatures = await cip30Wallet.signTx(tx);
  tx.addSignatures(signatures);
  const txId = await cip30Wallet
    .submitTx(tx)
    .then((txId$Helios) => txId$Helios.hex);

  addBreadcrumb("waiting for confirmation...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for confirmation..."
      ratio={steps.waitingForConfirmation.ratio}
      expectedDuration={steps.waitingForConfirmation.duration}
    />
  );

  await waitUntil(async () => {
    const status = await httpGet$GetTxStatus(`/api/v1/tx-status/${txId}`);
    return status.ok;
  });

  addBreadcrumb("waiting for indexer...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for indexer..."
      ratio={steps.waitingForIndexer.ratio}
      expectedDuration={steps.waitingForIndexer.duration}
    />
  );

  await waitUntil(async () => {
    const newArtwork = await httpGet$GetArtwork(
      `/api/v1/artwork/${artwork.artworkId}`
    );
    return (
      newArtwork.utxo?.datum.type === "artwork" &&
      newArtwork.utxo.datum.value.mintFee === formData.mintPrice &&
      newArtwork.utxo.datum.value.numEditions === formData.numEditions
    );
  });

  await shamelesslyRevalidateEverything();
  const assetFingerprint = getAssetFingerprint(artwork);
  const poolPmUrl = assetFingerprint
    ? getPoolPmUrl(assetFingerprint)
    : undefined;

  addBreadcrumb("showing success message");
  yield* waitModal<void>((resolve) => (
    <ModalSuccess
      open
      icon={<MdFavorite />}
      content="Your Artwork has been updated successfully!"
      buttons={[
        {
          content: "View on Pool.pm",
          onClick: poolPmUrl
            ? { type: "open", href: poolPmUrl, newTab: true }
            : null,
          isPrimary: true,
        },
        {
          content: "Close",
          onClick: "close",
        },
      ]}
      onClose={resolve}
    />
  ));

  Sentry.captureEvent({
    message: "runFlow$UpdateMintingDetails",
    level: "info",
  });
}

const addBreadcrumb = (msg: string) => {
  const terms = [
    "runFlow$UpdateMintingDetails",
    Date.now(),
    encodeURIComponent(msg),
  ];
  Sentry.addBreadcrumb({ message: terms.join(" - ") });
};
